import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaUser, FaCog, FaSignOutAlt, FaChevronDown } from 'react-icons/fa';
import CustomIcon from './CustomIcon';
import MoonLogo from './MoonLogo';
import axios from 'axios';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUser, setIsUser] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userCoins, setUserCoins] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const profileDropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const eventsDropdownRef = useRef(null);
  const eventsButtonRef = useRef(null);
  const eventsDropdownContentRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  useEffect(() => {
    // Admin token kontrolü
    const checkAdmin = () => {
      const token = localStorage.getItem('adminToken');
      setIsAdmin(!!token);
    };

    // User token kontrolü
    const checkUser = () => {
      const token = localStorage.getItem('userToken');
      const user = localStorage.getItem('userData');
      setIsUser(!!token);
      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          setUserData(parsedUser);
          // Set coins from userData if available
          if (parsedUser.coins !== undefined) {
            setUserCoins(parsedUser.coins);
          }
        } catch (e) {
          setUserData(null);
          setUserCoins(0);
        }
      } else {
        setUserData(null);
        setUserCoins(0);
      }
    };

    // İlk kontrol
    checkAdmin();
    checkUser();

    // Storage değişikliklerini dinle (logout/login için)
    const handleStorageChange = (e) => {
      if (e.key === 'adminToken') {
        checkAdmin();
      }
      if (e.key === 'userToken' || e.key === 'userData') {
        checkUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Custom event için listener (aynı tab'de logout/login için)
    const handleAdminTokenChanged = () => {
      checkAdmin();
    };
    
    const handleUserTokenChanged = () => {
      checkUser();
    };
    
    window.addEventListener('adminTokenChanged', handleAdminTokenChanged);
    window.addEventListener('userTokenChanged', handleUserTokenChanged);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminTokenChanged', handleAdminTokenChanged);
      window.removeEventListener('userTokenChanged', handleUserTokenChanged);
    };
  }, []);

  useEffect(() => {
    // Logo'yu yükle
    const fetchLogo = async () => {
      try {
        const response = await axios.get('/api/logo');
        if (response.data.logo_url) {
          setLogoUrl(response.data.logo_url);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };

    fetchLogo();
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setEventsDropdownOpen(false);
  }, [location.pathname]);

  // Close desktop events dropdown when mobile menu opens
  useEffect(() => {
    if (mobileMenuOpen && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setEventsDropdownOpen(false);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    // Coin bilgisini yükle
    const fetchUserCoins = async () => {
      if (!isUser) {
        setUserCoins(0);
        return;
      }

      const token = localStorage.getItem('userToken');
      if (!token) return;

      try {
        const response = await axios.get('/api/users/me/coins', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserCoins(response.data.coins || 0);
        
        // Update userData with coins
        if (userData) {
          const updatedUserData = { ...userData, coins: response.data.coins || 0 };
          setUserData(updatedUserData);
          localStorage.setItem('userData', JSON.stringify(updatedUserData));
        }
      } catch (error) {
        console.error('Error fetching user coins:', error);
      }
    };

    fetchUserCoins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUser]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (profileDropdownOpen && profileButtonRef.current) {
      const buttonRect = profileButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + 8, // 8px margin (mt-2)
        right: window.innerWidth - buttonRect.right
      });
    }
  }, [profileDropdownOpen]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check profile dropdown
      if (profileDropdownOpen) {
        if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target) &&
            profileButtonRef.current && !profileButtonRef.current.contains(event.target)) {
          setProfileDropdownOpen(false);
        }
      }
      
      // Check events dropdown (desktop only)
      if (eventsDropdownOpen) {
        const isClickInsideButton = eventsButtonRef.current && eventsButtonRef.current.contains(event.target);
        const isClickInsideDropdown = eventsDropdownContentRef.current && eventsDropdownContentRef.current.contains(event.target);
        
        // If click is not inside button or dropdown, close it
        // But don't close if clicking on a link inside dropdown
        if (!isClickInsideButton && !isClickInsideDropdown) {
          setEventsDropdownOpen(false);
        }
      }
    };

    // Use a small delay to allow link clicks to work first
    if (profileDropdownOpen || eventsDropdownOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [profileDropdownOpen, eventsDropdownOpen]);

  // Close mobile menu when clicking outside (mobile only)
  useEffect(() => {
    const handleMobileMenuClickOutside = (event) => {
      if (mobileMenuOpen) {
        const isClickInsideMenu = mobileMenuRef.current && mobileMenuRef.current.contains(event.target);
        const isClickOnButton = mobileMenuButtonRef.current && mobileMenuButtonRef.current.contains(event.target);
        
        if (!isClickInsideMenu && !isClickOnButton) {
          setMobileMenuOpen(false);
          setEventsDropdownOpen(false);
        }
      }
    };

    if (mobileMenuOpen) {
      // Use a small delay to allow menu button clicks to work first
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleMobileMenuClickOutside, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleMobileMenuClickOutside, true);
      };
    }
  }, [mobileMenuOpen]);

  // Get user initials for avatar
  const getUserInitials = (username) => {
    if (!username) return 'U';
    return username.charAt(0).toUpperCase();
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    setUserCoins(0);
    setProfileDropdownOpen(false);
    window.dispatchEvent(new Event('userTokenChanged'));
  };

  return (
    <div className="min-h-screen bg-gradient-dark animated-gradient overflow-x-hidden w-full">
      {/* Navigation */}
      <nav className="bg-navy-900/80 backdrop-blur-md border-b border-gold/30 sticky top-0 z-40 shadow-xl overflow-visible">
        <div className="container mx-auto px-4 py-4 overflow-x-hidden overflow-y-visible max-w-full relative z-40">
          <div className="flex items-center justify-between relative overflow-visible">
            <Link to="/" className="flex items-center ml-2 md:ml-8 lg:ml-12 flex-shrink-0" onClick={() => setMobileMenuOpen(false)}>
              {logoUrl && (
                <img
                  src={logoUrl && logoUrl.startsWith('http') ? logoUrl : logoUrl || ''}
                  alt="SAGUEX Logo"
                  className="h-10 md:h-12 object-contain"
                  onError={() => setLogoUrl(null)}
                />
              )}
            </Link>
            
            {/* Desktop Menu - Centered */}
            <div className="hidden lg:flex items-center gap-3 xl:gap-8 absolute left-1/2 transform -translate-x-1/2 overflow-visible">
              <Link 
                to="/" 
                className={`text-base md:text-lg font-semibold transition-all duration-300 ${
                  location.pathname === '/' 
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                    : 'text-white hover:text-gold'
                }`}
              >
                Ana Sayfa
              </Link>
              <Link 
                to="/sponsors" 
                className={`text-base md:text-lg font-semibold transition-all duration-300 ${
                  location.pathname === '/sponsors' 
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                    : 'text-white hover:text-gold'
                }`}
              >
                Sponsorlar
              </Link>
              <Link
                to="/moon"
                className="relative flex items-center"
                title="Moon - Kaldıraçlı bahis platformu"
              >
                <MoonLogo
                  size="sm"
                  className={`transition-transform duration-300 hover:scale-105 ${
                    location.pathname === '/moon' ? 'ring-2 ring-[#1AD4FF] ring-offset-2 ring-offset-navy-900' : ''
                  }`}
                />
                <span className="absolute -top-2 -right-2 rounded-full bg-gradient-to-br from-[#1AD4FF] to-[#009ACA] px-1.5 py-0.5 text-[8px] font-bold uppercase leading-none text-[#011825] shadow-md">
                  Yeni
                </span>
              </Link>
              <Link
                to="/spin-event"
                className={`flex items-center gap-2 text-base md:text-lg font-semibold transition-all duration-300 ${
                  location.pathname.startsWith('/spin-event')
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                    : 'text-white hover:text-gold'
                }`}
              >
                <CustomIcon name="cevrim" className="w-5 h-5 md:w-6 md:h-6" />
                <span>Çevrim Etkinliği</span>
              </Link>
              <Link 
                to="/market" 
                className={`text-base md:text-lg font-semibold transition-all duration-300 ${
                  location.pathname === '/market' 
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                    : 'text-white hover:text-gold'
                }`}
              >
                Market
              </Link>
              
              {/* Etkinlikler Dropdown - Desktop Only */}
              <div className="relative hidden lg:block" ref={eventsDropdownRef}>
                <button
                  ref={eventsButtonRef}
                  onClick={() => setEventsDropdownOpen(!eventsDropdownOpen)}
                  className={`flex items-center gap-1 text-base md:text-lg font-semibold transition-all duration-300 ${
                    location.pathname.startsWith('/games') || location.pathname === '/promo-code'
                      ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold'
                  }`}
                >
                  Etkinlikler
                  <FaChevronDown className={`w-3 h-3 transition-transform duration-300 ${eventsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {eventsDropdownOpen && eventsButtonRef.current && !mobileMenuOpen && (() => {
                  // Only render on desktop (lg breakpoint = 1024px)
                  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                    return null;
                  }
                  
                  const buttonRect = eventsButtonRef.current.getBoundingClientRect();
                  const dropdownTop = buttonRect.bottom + 8;
                  const dropdownLeft = buttonRect.left + (buttonRect.width / 2) - 96;
                  
                  return createPortal(
                    <div 
                      ref={eventsDropdownContentRef}
                      className="fixed w-48 bg-navy-800 rounded-lg shadow-xl border border-gold/30 overflow-hidden hidden lg:block" 
                      style={{ 
                        zIndex: 10000,
                        top: `${dropdownTop}px`,
                        left: `${dropdownLeft}px`
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setEventsDropdownOpen(false);
                        navigate('/games');
                      }}
                      className={`block px-4 py-3 transition-colors cursor-pointer ${
                        location.pathname.startsWith('/games')
                          ? 'bg-gold/20 text-gold'
                          : 'text-white hover:bg-navy-700 hover:text-gold'
                      }`}
                    >
                      Saguex Originals
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setEventsDropdownOpen(false);
                        navigate('/promo-code');
                      }}
                      className={`block px-4 py-3 transition-colors cursor-pointer ${
                        location.pathname === '/promo-code'
                          ? 'bg-gold/20 text-gold'
                          : 'text-white hover:bg-navy-700 hover:text-gold'
                      }`}
                    >
                      Puan Kodu
                    </div>
                    </div>,
                    document.body
                  );
                })()}
              </div>
              
              <Link 
                to="/tickets" 
                className={`text-base md:text-lg font-semibold transition-all duration-300 ${
                  location.pathname === '/tickets'
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                    : 'text-white hover:text-gold'
                }`}
              >
                Biletler
              </Link>
              <Link 
                to="/contact" 
                className={`text-base md:text-lg font-semibold transition-all duration-300 ${
                  location.pathname === '/contact' 
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                    : 'text-white hover:text-gold'
                }`}
              >
                İletişim
              </Link>
            </div>

            {/* Right Side - User Section */}
            <div className="hidden lg:flex items-center gap-3 md:gap-4 flex-shrink-0 mr-2 md:mr-8 lg:mr-12">
              {isUser ? (
                <>
                  <Link 
                    to="/market"
                    className="flex items-center gap-2 bg-gold/20 px-3 py-1.5 rounded-lg border border-gold/30 hover:bg-gold/30 transition-colors cursor-pointer"
                  >
                    <span className="text-gold font-bold text-sm md:text-base">
                      💰 {userCoins || userData?.coins || 0}
                    </span>
                    <span className="text-gold/80 text-xs md:text-sm font-medium">
                      Sagu Coin
                    </span>
                  </Link>
                  
                  {/* Profile Avatar with Dropdown */}
                  <div className="relative" ref={profileDropdownRef}>
                    <button
                      ref={profileButtonRef}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setProfileDropdownOpen(!profileDropdownOpen);
                      }}
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-gold to-yellow-500 text-navy-900 font-bold text-lg hover:from-yellow-500 hover:to-gold transition-all duration-300 shadow-lg hover:shadow-gold/50 focus:outline-none focus:ring-2 focus:ring-gold relative"
                    >
                      {getUserInitials(userData?.username)}
                    </button>
                    
                    {/* Dropdown Menu */}
                    {profileDropdownOpen && (
                      <div 
                        className="fixed w-48 bg-navy-800 rounded-lg shadow-xl border border-gold/30 overflow-hidden" 
                        style={{ 
                          zIndex: 10000,
                          top: `${dropdownPosition.top}px`,
                          right: `${dropdownPosition.right}px`
                        }}
                      >
                        <div className="py-1">
                          <Link
                            to="/profile"
                            onClick={() => setProfileDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 text-white hover:bg-navy-700 transition-colors"
                          >
                            <FaUser className="text-gold" />
                            <span>Profil</span>
                          </Link>
                          <Link
                            to="/settings"
                            onClick={() => setProfileDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 text-white hover:bg-navy-700 transition-colors"
                          >
                            <FaCog className="text-gold" />
                            <span>Ayarlar</span>
                          </Link>
                          <div className="border-t border-gold/20 my-1"></div>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-red-500/20 transition-colors text-left"
                          >
                            <FaSignOutAlt className="text-red-400" />
                            <span>Çıkış Yap</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 md:gap-4">
                  <Link 
                    to="/login" 
                    className="text-white hover:text-gold text-sm md:text-base font-medium transition-colors"
                  >
                    Giriş
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-all duration-300 shadow-gold text-sm md:text-base"
                  >
                    Kayıt Ol
                  </Link>
                </div>
              )}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-500 text-white font-bold px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-all duration-300 text-sm md:text-base"
                >
                  Admin
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              ref={mobileMenuButtonRef}
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                // Mobil menü kapatıldığında etkinlikler dropdown'ını da kapat
                if (mobileMenuOpen) {
                  setEventsDropdownOpen(false);
                }
              }}
              className="lg:hidden text-gold hover:text-gold-light transition-colors p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <FaTimes className="w-6 h-6" />
              ) : (
                <FaBars className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div ref={mobileMenuRef} className="lg:hidden mt-4 pb-4 border-t border-gold/20 pt-4 animate-fadeIn">
              <div className="flex flex-col gap-3">
                <Link 
                  to="/" 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                    location.pathname === '/' 
                      ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold hover:bg-white/5'
                  }`}
                >
                  Ana Sayfa
                </Link>
                <Link 
                  to="/sponsors" 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                    location.pathname === '/sponsors' 
                      ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold hover:bg-white/5'
                  }`}
                >
                  Sponsorlar
                </Link>
                <Link
                  to="/moon"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`flex items-center gap-2.5 py-2 px-2 rounded-lg transition-all duration-300 ${
                    location.pathname === '/moon' ? 'bg-[#1AD4FF]/10' : 'hover:bg-white/5'
                  }`}
                >
                  <MoonLogo size="sm" />
                  <span className="text-base font-semibold text-white">Moon</span>
                  <span className="rounded-full bg-gradient-to-br from-[#1AD4FF] to-[#009ACA] px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-[#011825]">
                    Yeni
                  </span>
                </Link>
                <Link
                  to="/spin-event"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`flex items-center gap-2 text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                    location.pathname.startsWith('/spin-event')
                      ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold hover:bg-white/5'
                  }`}
                >
                  <CustomIcon name="cevrim" className="w-5 h-5" />
                  <span>Çevrim Etkinliği</span>
                </Link>
                <Link 
                  to="/market" 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                    location.pathname === '/market' 
                      ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold hover:bg-white/5'
                  }`}
                >
                  Market
                </Link>
                
                {/* Etkinlikler Mobile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setEventsDropdownOpen(!eventsDropdownOpen)}
                    className={`flex items-center justify-between w-full text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                      location.pathname.startsWith('/games') || location.pathname === '/promo-code'
                        ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                        : 'text-white hover:text-gold hover:bg-white/5'
                    }`}
                  >
                    <span>Etkinlikler</span>
                    <FaChevronDown className={`w-3 h-3 transition-transform duration-300 ${eventsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {eventsDropdownOpen && (
                    <div className="pl-4 mt-2 space-y-1 animate-fadeIn">
                      <Link
                        to="/games"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setEventsDropdownOpen(false);
                        }}
                        className={`block text-sm transition-all duration-300 py-2 px-3 rounded-lg ${
                          location.pathname.startsWith('/games')
                            ? 'text-gold bg-gold/10'
                            : 'text-white/80 hover:text-gold hover:bg-white/5'
                        }`}
                      >
                        Saguex Originals
                      </Link>
                      <Link
                        to="/promo-code"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setEventsDropdownOpen(false);
                        }}
                        className={`block text-sm transition-all duration-300 py-2 px-3 rounded-lg ${
                          location.pathname === '/promo-code'
                            ? 'text-gold bg-gold/10'
                            : 'text-white/80 hover:text-gold hover:bg-white/5'
                        }`}
                      >
                        Puan Kodu
                      </Link>
                    </div>
                  )}
                </div>
                
                <Link 
                  to="/tickets" 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                    location.pathname === '/tickets'
                      ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold hover:bg-white/5'
                  }`}
                >
                  Biletler
                </Link>
                <Link 
                  to="/contact" 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setEventsDropdownOpen(false);
                  }}
                  className={`text-base font-semibold transition-all duration-300 py-2.5 px-2 rounded-lg ${
                    location.pathname === '/contact' 
                      ? 'text-gold bg-gold/10 drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' 
                      : 'text-white hover:text-gold hover:bg-white/5'
                  }`}
                >
                  İletişim
                </Link>
                {isUser ? (
                  <div className="flex flex-col gap-3 pt-3 mt-3 border-t border-gold/20">
                    <Link
                      to="/market"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setEventsDropdownOpen(false);
                      }}
                      className="flex items-center gap-2 bg-gold/20 px-4 py-2.5 rounded-lg border border-gold/30 hover:bg-gold/30 transition-all duration-300 cursor-pointer"
                    >
                      <span className="text-gold font-bold text-base">
                        💰 {userCoins || userData?.coins || 0}
                      </span>
                      <span className="text-gold/80 text-sm font-medium">
                        Sagu Coin
                      </span>
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setEventsDropdownOpen(false);
                      }}
                      className="flex items-center gap-3 text-white hover:text-gold text-base transition-all duration-300 font-medium py-2.5 px-2 rounded-lg hover:bg-white/5"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-yellow-500 text-navy-900 font-bold flex items-center justify-center text-base shadow-lg">
                        {getUserInitials(userData?.username)}
                      </div>
                      <span>Profil</span>
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setEventsDropdownOpen(false);
                      }}
                      className="flex items-center gap-3 text-white hover:text-gold text-base transition-all duration-300 font-medium py-2.5 px-2 rounded-lg hover:bg-white/5"
                    >
                      <FaCog className="text-gold text-lg" />
                      <span>Ayarlar</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                        setEventsDropdownOpen(false);
                      }}
                      className="flex items-center gap-3 text-red-400 hover:text-red-300 text-base transition-all duration-300 font-medium py-2.5 px-2 rounded-lg hover:bg-red-500/10 text-left"
                    >
                      <FaSignOutAlt className="text-lg" />
                      <span>Çıkış Yap</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 pt-3 mt-3 border-t border-gold/20">
                    <Link 
                      to="/login" 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setEventsDropdownOpen(false);
                      }}
                      className="text-white hover:text-gold text-base font-medium transition-all duration-300 py-2.5 px-2 rounded-lg hover:bg-white/5 text-center"
                    >
                      Giriş
                    </Link>
                    <Link 
                      to="/register" 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setEventsDropdownOpen(false);
                      }}
                      className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-4 py-3 rounded-lg transition-all duration-300 shadow-lg text-base text-center"
                    >
                      Kayıt Ol
                    </Link>
                  </div>
                )}
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setEventsDropdownOpen(false);
                    }}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-500 text-white font-bold px-4 py-3 rounded-lg transition-all duration-300 text-base text-center shadow-lg mt-2"
                  >
                    Admin
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Page Content */}
      <main className="overflow-x-hidden w-full">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-sm border-t border-gold/20 mt-20 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-300 text-center md:text-left">&copy; 2026 SAGUEX. Tüm hakları saklıdır.</p>
            <a 
              href="https://t.me/kiezsadev" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative text-center md:text-right transition-all duration-300"
            >
              <span className="text-gray-400 text-xs font-light italic">
                designed by{' '}
                <span className="text-gold font-semibold not-italic tracking-wider relative inline-block">
                  <span className="relative z-10">Kiezsa</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-gold/20 via-yellow-500/20 to-gold/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></span>
                </span>
              </span>
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-gold to-yellow-500 group-hover:w-full transition-all duration-300"></span>
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Layout;
